/**
 * Copyright (c) 2021 - 2023, Kodit Doo
 * Created by kodit-marko on 03.01.2023.
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FilterChipDto,
  GetPdvEvidencijaPojedinacnaTableQuery,
  IPaginatedResultOfRacunTableDto,
  IPdvEvidencijaPojedinacnaTableDto,
  PdvEvidencijaStatus,
  PdvEvidencijeClient,
  SefCancelPdvEvidencijaPojedinacnaCommand,
  SefEvidentirajPdvEvidencijaPojedinacnaCommand,
  TipPristupa,
} from '@kodit/core/data-api';
import {
  ActionMode,
  ActionType,
  OnTableInit,
  SelectMode,
  TableConfig,
} from '@kodit/core/shared-ui';
import {
  AlertService,
  ConfigService,
  LocationService,
} from '@kodit/core/services';
import { Subscription } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { Store } from '@ngrx/store';
import { selectFilterByKey } from 'libs/core/shared-subforms/src/lib/racun/state/filters/filter.selectors';
import { nameofFactory, toCamelCase } from '@kodit/core/shared';
import {
  clearFilter,
  setFilter,
} from 'libs/core/shared-subforms/src/lib/racun/state/filters/filter.actions';
import { PdvEvidencijaPojedinacnaFilterComponent } from './pdv-evidencija-pojedinacna-filter/pdv-evidencija-pojedinacna-filter.component';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'kodit-pdv-evidencija-pojedinacna-table',
  templateUrl: './pdv-evidencija-pojedinacna-table.component.html',
  styleUrls: ['./pdv-evidencija-pojedinacna-table.component.scss'],
})
export class PdvEvidencijaPojedinacnaTableComponent
  implements OnInit, OnDestroy, OnTableInit {
  /** Props */
  isLoading = true;
  paginatedData: IPaginatedResultOfRacunTableDto | undefined;
  tableConfig!: TableConfig;
  numberOfRowsDisplayed: number | undefined = 0;
  tableArray: IPdvEvidencijaPojedinacnaTableDto[] | undefined = [];
  chipItems: FilterChipDto[] = [];

  private _subs: Subscription = new Subscription();
  filters!: GetPdvEvidencijaPojedinacnaTableQuery;
  private _skipFirst = 0;
  isFirstLoad = true;
  private _isBusy = false;

  constructor(
    private _client: PdvEvidencijeClient,
    private _alertService: AlertService,
    private _locationService: LocationService,
    private _confirmationService: ConfirmationService,
    private _storage: Store,
    private _configService: ConfigService
  ) {
    this.tableArray = [];
  }

  ngOnInit(): void {
    this.setTableConfig();

    // reload tabele kada se filter promeni
    this._subs.add(
      this._storage
        .select(selectFilterByKey('PDV|Pojedinacne'))
        .subscribe((data) => {
          if (data) {
            this.filters = JSON.parse(data);
            this._skipFirst = this.filters.pageNumber ?? 0;
            this.numberOfRowsDisplayed = this.filters.pageSize ?? 0;
          } else {
            this.filters = new GetPdvEvidencijaPojedinacnaTableQuery({
              pageNumber: this._skipFirst,
              pageSize: this.numberOfRowsDisplayed,
            });
          }
          this._load();
        })
    );
  }

  async setTableConfig(): Promise<void> {
    this.tableConfig = new TableConfig({
      isLazy: true,
      lazyCallbackFunction: (event) => {
        if (!this.isFirstLoad) {
          this._skipFirst = event.first!;
          this.numberOfRowsDisplayed = event.rows;
        }
        this.updateFilterDataAndReload();
        if (this.isFirstLoad) {
          this.isFirstLoad = false;
        }
      },
      selectMode: SelectMode.SINGLE,
      tableHeader: 'Pojedinačne pdv evidencije',
      multiDeleteMessage:
        'Da li ste sigurni da želite da obrišete izabrane pdv evidenciju?',
      deleteMessage:
        'Da li ste sigurni da želite da obrišete pdv evidenciju {param}?',
      deleteMessageParams: 'broj',
      onChipRemove: (chips: FilterChipDto) => this._handleChipRemove(chips),
      columns: [
        {
          header: 'Tip',
          type: 'badge',
          field: 'tipDto',
          subField: 'tipStr',
          styleClassField: 'tipBadgeStr',
          styleClass: 'tip-dokumenta-evidencije',
          tooltipField: 'opis',
        },
        {
          field: 'broj',
          header: 'Broj',
          type: 'text',
          columns: [
            {
              field: 'brojDokumenta',
              header: 'Vezani račun',
              type: 'text',
            },
          ],
        },
        {
          field: 'pdv',
          header: 'PDV',
          type: 'currency',
        },
        {
          field: 'iznosNaknade',
          header: 'Iznos naknade',
          type: 'currency',
        },
        {
          field: 'datumPromeneStatusa',
          header: 'Datum promene statusa',
          type: 'text',
        },
        {
          field: 'periodDto',
          subField: 'periodStr',
          header: 'Period',
          styleClass: 'pdv-period',
          styleClassField: 'periodBadgeStr',
          tooltipField: 'opis',
          type: 'badge',
        },
        {
          field: 'statusDto',
          subField: 'statusStr',
          header: 'Status',
          styleClass: 'pdv-status',
          styleClassField: 'statusBadgeStr',
          tooltipField: 'opis',
          type: 'badge',
        },
      ],
      headerActions: [
        {
          mode: ActionMode.SINGLE,
          type: ActionType.CUSTOM,
          tipPristupa: TipPristupa.PDV_CRUD,
          icon: 'fa-solid fa-eye',
          hasAccessTooltip: 'Pregledajte korigovanu pdv evidenciju',
          noAccessTooltip:
            'Nemate ovlašćenja za pregled Korigovane pdv evidencije',
          shouldDisableWhenSefInactive: false,
          callback: (index: number) => {
            this._locationService
              .routeToPdvEvidencijePojedinacnaKorigovana(
                this._getPaginatedItem(index).id!
              )
              .then();
          },
          shouldDisplayByCondition: (
            rowData: IPdvEvidencijaPojedinacnaTableDto
          ) => {
            return this._shouldDisplayPregledKorigovane(
              rowData.statusDto!.status!
            );
          },
        },
        {
          mode: ActionMode.SINGLE,
          type: ActionType.CUSTOM,
          tipPristupa: TipPristupa.PDV_CRUD,
          icon: 'fa-light fa-xmark-large',
          hasAccessTooltip: 'Poništite pdv evidenciju',
          noAccessTooltip: 'Nemate ovlašćenja za poništavanje pdv evidencije',
          shouldDisableWhenSefInactive: true,
          callback: (index: number) => {
            this._confirmationService.confirm({
              header: 'Potvrda poništavanja',
              message: 'Da li želite da poništite evidenciju?',
              acceptLabel: 'Poništi',
              rejectLabel: 'Odustani',
              rejectButtonStyleClass: 'p-button-outlined',
              accept: () => {
                this._subs.add(
                  this._client
                    .sefCancelPdvEvidencijaPojedinacna(
                      new SefCancelPdvEvidencijaPojedinacnaCommand({
                        evidencijaId: this._getPaginatedItem(index).id,
                      })
                    )
                    .subscribe((res) => {
                      if (res.succeeded) {
                        this._alertService.addSuccessMsg(res.messages![0]);
                        this._load();
                      }
                    })
                );
              },
            });
          },
          shouldDisplayByCondition: (
            rowData: IPdvEvidencijaPojedinacnaTableDto
          ) => {
            return this._shouldDisplayCancel(rowData.statusDto!.status!);
          },
        },
      ],
      advancedFilter: {
        component: PdvEvidencijaPojedinacnaFilterComponent,
        data: new GetPdvEvidencijaPojedinacnaTableQuery(),
      },
    });
  }

  private _handleChipRemove(removedChip: FilterChipDto) {
    const nameof = nameofFactory<GetPdvEvidencijaPojedinacnaTableQuery>();
    const nameOfStatusi = nameof('statusi');
    const nameOfTipovi = nameof('tipoviDokumenta');

    if (removedChip.key!.toLowerCase() === nameOfStatusi.toLowerCase()) {
      const idx = (this.tableConfig.advancedFilter!
        .data as GetPdvEvidencijaPojedinacnaTableQuery).statusi!.findIndex(
        (x) => x === removedChip.defaultValue
      );
      (this.tableConfig.advancedFilter!
        .data as GetPdvEvidencijaPojedinacnaTableQuery).statusi!.splice(idx, 1);
    } else if (removedChip.key!.toLowerCase() === nameOfTipovi.toLowerCase()) {
      const idx = (this.tableConfig.advancedFilter!
        .data as GetPdvEvidencijaPojedinacnaTableQuery).tipoviDokumenta!.findIndex(
        (x) => x === removedChip.defaultValue
      );
      (this.tableConfig.advancedFilter!
        .data as GetPdvEvidencijaPojedinacnaTableQuery).tipoviDokumenta!.splice(
        idx,
        1
      );
    } else {
      this.tableConfig.advancedFilter!.data[toCamelCase(removedChip.key!)] =
        removedChip.defaultValue;
    }
    this._updateFilters();
  }

  private _updateFilters() {
    if (this._suFilteriPrazni()) {
      this._storage.dispatch(
        clearFilter({
          key: 'PDV|Pojedinacne',
        })
      );
      return;
    }

    this._storage.dispatch(
      setFilter({
        key: 'PDV|Pojedinacne',
        filter: JSON.stringify(this.filters),
      })
    );
  }
  private _suFilteriPrazni() {
    for (const prop of Object.keys(this.filters) as Array<
      keyof GetPdvEvidencijaPojedinacnaTableQuery
    >) {
      if (this.filters[prop]) {
        return false;
      }
    }
    return true;
  }

  private updateFilterDataAndReload() {
    if (
      (this._skipFirst !== this.filters.pageNumber ||
        this.numberOfRowsDisplayed !== this.filters.pageSize) &&
      !this.isFirstLoad
    ) {
      this.filters.pageNumber = this._skipFirst;
      this.filters.pageSize = this.numberOfRowsDisplayed;
      this._updateStorage();
    }
  }

  private _updateStorage() {
    this._storage.dispatch(
      setFilter({
        key: 'PDV|Pojedinacne',
        filter: JSON.stringify(this.filters),
      })
    );
  }

  private _load() {
    if (this._isBusy) {
      return;
    }

    this._configService.setIsBusy = true;
    this._isBusy = true;

    if (!this.filters) {
      this.filters = new GetPdvEvidencijaPojedinacnaTableQuery({
        pageNumber: this._skipFirst,
        pageSize: this.numberOfRowsDisplayed,
      });
    }

    this.isLoading = true;
    this._subs.add(
      this._client
        .getForTable(this.filters as GetPdvEvidencijaPojedinacnaTableQuery)
        .pipe(
          finalize(() => {
            this._configService.setIsBusy = false;
            this._isBusy = false;
            this.isLoading = false;
          })
        )
        .subscribe(
          (res) => {
            this.paginatedData = res;
            this.tableArray = res.data;
            this.chipItems = res.activeFilters!;
            this.tableConfig.advancedFilter!.data = this.filters;
          },
          (error) => {
            //this._configService.setIsBusy = false;
          }
        )
    );
  }
  private _routeToEdit(rowIndex: number) {
    const entity = this._getPaginatedItem(rowIndex);
    this._locationService
      .routeToPdvEvidencijePojedinacnaUpdate(entity.id!)
      .then();
  }

  private _routeToCreate() {
    this._locationService.routeToPdvEvidencijePojedinacnaCreate().then();
  }

  private _goToVezaniRacunInfo(rowIndex: number, subIndex: number) {
    // const racun = this.racuniTable[rowIndex - this.skipFirst].vezaniRacuniDto[
    //   subIndex
    //   ];
    // this._locationService.routeToRacunKartica(
    //   racun.racunId,
    //   racun.tip,
    //   racun.vrsta
    // );
  }

  private _getPaginatedIndex(index: number): number {
    return index - this._skipFirst;
  }

  private _getPaginatedItem(index: number): IPdvEvidencijaPojedinacnaTableDto {
    return this.tableArray![this._getPaginatedIndex(index)];
  }

  private _shouldDisplayEdit(status: PdvEvidencijaStatus) {
    return status === PdvEvidencijaStatus.U_PRIPREMI;
  }

  private _shouldDisplayEvidentiraj(status: PdvEvidencijaStatus) {
    return status === PdvEvidencijaStatus.U_PRIPREMI;
  }

  private _shouldDisplayCancel(status: PdvEvidencijaStatus) {
    return status === PdvEvidencijaStatus.EVIDENTIRANO;
  }

  private _shouldDisplayKoriguj(status: PdvEvidencijaStatus) {
    return status === PdvEvidencijaStatus.EVIDENTIRANO;
  }

  private _shouldDisplayPregledKorigovane(status: PdvEvidencijaStatus) {
    return status === PdvEvidencijaStatus.KORIGOVANO;
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
  }
}
